import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Gift, Check, ArrowRight, Sparkles } from "lucide-react";
import { startCheckout, trackEvent } from "@/lib/checkout";

const BONUSES = [
  {
    tag: "Bonus 01",
    title: "100 Hustle Mindset Reels",
    desc: "A curated vault of 100 scroll-stopping, high-fashion faceless reels ready to post and monetize.",
    value: "$27",
    img: "/assets/bonus-hustle-reels.jpg",
    fit: "contain",
    alt: "Bonus 100 Hustle Mindset Reels & Posts — ready-to-post faceless motivational Instagram Reels, editable in Canva with MRR & PLR, to monetize social media and make money online",
  },
  {
    tag: "Bonus 02",
    title: "350 Winning Hooks",
    desc: "The uncrackable formula for massive engagement — 350 ready-to-use viral hooks, sorted into 7 categories, that stop the scroll instantly.",
    value: "$37",
    img: "/assets/bonus-content-hooks.jpg",
    fit: "contain",
    alt: "Bonus 02 — 350 Winning Hooks: your free bonus, a master collection of viral social media hooks sorted into 7 categories for massive engagement and explosive growth",
  },
  {
    tag: "Bonus 03",
    title: "200+ Luxe Life Reels",
    desc: "Over 200 aesthetic luxury-lifestyle reels with motivational overlays to build an elite, aspirational brand.",
    value: "$27",
    img: "/assets/bonus-luxe-woman.jpg",
    fit: "contain",
    alt: "Bonus 200+ Luxe Life Reels — PLR & MRR faceless luxury woman lifestyle videos for passive income through digital products, Master Resell Rights and Private Label Rights",
  },
  
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function BonusSection() {
  return (
    <motion.section
      data-testid="bonus-section"
      className="relative py-24 md:py-32 bg-[#FFF3E8] border-y border-[#F59E0B]/20 overflow-hidden"
      onViewportEnter={() => trackEvent("view_bonus", { section: "free_bonuses", bonus_count: 3 })}
      viewport={{ once: true, amount: 0.3 }}
    >
      {/* Radial gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#F59E0B]/8 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="inline-flex items-center gap-2 border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-5 py-2 mb-8"
          >
            <Gift className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#F59E0B]">
              Free exclusive bonuses
            </span>
          </motion.div>

          <motion.h2
            initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight"
          >
            Order Today & Unlock <span className="gold-shimmer">3 Elite Bonuses</span> — Free
          </motion.h2>

          <motion.p
            initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} variants={fadeUp}
            className="mt-6 text-lg text-[#666666]"
          >
            Reserved for serious creators only. These premium vaults are sold separately — but
            they're yours <span className="text-[#F59E0B] font-semibold">100% free</span> when you
            grab the bundle now.
          </motion.p>

          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} custom={3} variants={fadeUp}
            className="mt-8"
          >
            <Link
              to="/bonuses"
              data-testid="see-all-bonuses-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#4338CA] border-b border-[#4338CA]/40 pb-1 hover:border-[#4338CA] transition-colors"
            >
              See all bonuses in detail
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>

        {/* Bonus cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BONUSES.map((b, i) => (
            <motion.div
              key={i}
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              custom={i} variants={fadeUp}
              data-testid={`bonus-card-${i}`}
              className="group relative bg-[#FFFFFF] border border-[#EFE3D5] hover:border-[#F59E0B]/60 transition-colors duration-300 flex flex-col"
            >
              {/* FREE ribbon */}
              <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5 bg-[#F59E0B] text-black text-xs font-extrabold uppercase tracking-widest px-3 py-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Free
              </div>

              {/* Image */}
              <div className="relative overflow-hidden bg-[#F5EFE7] aspect-[4/3] border-b border-[#EFE3D5]">
                <img
                  src={b.img}
                  alt={b.alt}
                  data-testid={`bonus-image-${i}`}
                  loading="lazy"
                  className={`relative z-10 w-full h-full ${b.fit === "contain" ? "object-contain" : "object-cover group-hover:scale-105"} transition-transform duration-500`}
                />
                {/* Smoke / vignette fade into the charcoal card */}
                <div
                  className="absolute inset-0 z-20 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, rgba(10,10,10,0) 35%, rgba(10,10,10,0.55) 72%, rgba(10,10,10,0.95) 100%)",
                  }}
                />
                <div
                  className="absolute inset-0 z-20 pointer-events-none"
                  style={{
                    boxShadow: "inset 0 0 70px 30px rgba(0,0,0,0.9)",
                  }}
                />
                <div className="absolute inset-0 z-20 bg-gradient-to-t from-[#FFFFFF] via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Body */}
              <div className="p-7 flex flex-col flex-1">
                <span className="text-xs uppercase tracking-[0.25em] text-[#F59E0B]">{b.tag}</span>
                <h3 className="mt-3 font-serif-display text-2xl font-semibold">{b.title}</h3>
                <p className="mt-4 text-sm text-[#666666] leading-relaxed flex-1">{b.desc}</p>

                <div className="mt-6 flex items-center justify-between border-t border-[#EFE3D5] pt-5">
                  <span className="text-xs uppercase tracking-widest text-[#666666]">Value</span>
                  <div className="flex items-center gap-2">
                    <span className="font-serif-display text-xl line-through text-[#8A8A8A]">{b.value}</span>
                    <span className="text-sm font-bold text-[#F59E0B] uppercase tracking-wider">Free</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Total value banner */}
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          className="mt-14 relative border border-[#F59E0B]/40 bg-gradient-to-r from-[#F59E0B]/5 via-transparent to-[#F59E0B]/5 p-8 md:p-10 text-center"
        >
          <p className="text-sm uppercase tracking-[0.25em] text-[#666666]">Total bonus value</p>
          <div className="mt-3 flex items-center justify-center gap-4">
            <span className="font-serif-display text-4xl md:text-5xl font-bold line-through text-[#8A8A8A]">$91</span>
            <span className="font-serif-display text-5xl md:text-6xl font-bold gold-shimmer">FREE</span>
          </div>
          <p className="mt-4 text-[#666666] max-w-xl mx-auto">
            Included at no extra cost with your <span className="text-[#232323] font-semibold">$27</span> bundle —
            today only. Prices go up once the free bonuses are pulled.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#666666]">
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F59E0B]" /> Instant access</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F59E0B]" /> Ready-to-post</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F59E0B]" /> Lifetime license</span>
          </div>

          <button
            data-testid="bonus-cta-button"
            onClick={startCheckout}
            className="btn-gold mt-10 inline-flex items-center justify-center gap-3 px-10 py-5 text-base sm:text-lg"
          >
            Claim The Bundle + 3 Free Bonuses
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </motion.section>
  );
}
